// src/components/results/ResultsDisplay.jsx
import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, TrendingUp, FileText, Lightbulb, Sparkles } from 'lucide-react';
import { downloadReport } from '@utils/exportReport';

export default function ResultsDisplay({ results, resumeName, jobTitle }) {
  const [activeTab, setActiveTab] = useState('keywords');

  if (!results) return null;

  // Calculate scores
  const atsScore = results.atsScore?.overall || results.scores?.overall || 0;
  const matchScore = results.keywordAnalysis?.relevanceScore || results.scores?.keywordMatch || 0;
  const matchedCount = results.keywordAnalysis?.matched?.length || results.keywords?.matched?.length || 0;
  const totalKeywords = (results.keywordAnalysis?.matched?.length || 0) + (results.keywordAnalysis?.missing?.length || 0) || 
                        (results.keywords?.matched?.length || 0) + (results.keywords?.missing?.length || 0) || 0;

  // Get data arrays
  const matchedKeywords = results.keywordAnalysis?.matched || results.keywords?.matched || [];
  const missingKeywords = (results.keywordAnalysis?.missing || results.keywords?.missing || []).slice(0, 20);
  const strengths = results.strengths || [];
  const weaknesses = results.weaknesses || [];
  const recommendations = results.recommendations || [];
  const aiSuggestions = results.aiEnhancedSuggestions || results.aiSuggestions || [];

  // Score color helper
  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreRingColor = (score) => {
    if (score >= 70) return 'text-emerald-500';
    if (score >= 50) return 'text-amber-500';
    return 'text-red-500';
  };

  return (
    <div className="space-y-8">
      {/* Header with branding */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 border border-purple-200 rounded-full mb-4">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-medium text-purple-700">Generative AI in Action</span>
        </div>
      </div>

      {/* Score Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* ATS Score */}
        <div className="bg-white rounded-2xl shadow-sm border p-6 text-center">
          <div className="relative inline-flex items-center justify-center w-32 h-32 mb-4">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-gray-200"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${(atsScore / 100) * 351.86} 351.86`}
                className={getScoreRingColor(atsScore)}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-3xl font-bold ${getScoreColor(atsScore)}`}>
                {atsScore}%
              </span>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">ATS Score</h3>
          <p className="text-sm text-gray-600">Resume compatibility</p>
        </div>

        {/* Match Score */}
        <div className="bg-white rounded-2xl shadow-sm border p-6 text-center">
          <div className="relative inline-flex items-center justify-center w-32 h-32 mb-4">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-gray-200"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${(matchScore / 100) * 351.86} 351.86`}
                className={getScoreRingColor(matchScore)}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-3xl font-bold ${getScoreColor(matchScore)}`}>
                {matchScore}%
              </span>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Match Score</h3>
          <p className="text-sm text-gray-600">Job alignment</p>
        </div>

        {/* Keywords Found */}
        <div className="bg-white rounded-2xl shadow-sm border p-6 text-center">
          <div className="relative inline-flex items-center justify-center w-32 h-32 mb-4">
            <div className="flex flex-col items-center justify-center bg-blue-50 rounded-full w-32 h-32">
              <span className="text-4xl font-bold text-blue-600">{matchedCount}/{totalKeywords}</span>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Keywords Found</h3>
          <p className="text-sm text-gray-600">Critical terms matched</p>
        </div>
      </div>

      {/* Detailed Analysis Section */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="border-b px-6 py-4">
          <h2 className="text-xl font-bold text-gray-900">Detailed Analysis</h2>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <nav className="flex gap-1 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('keywords')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'keywords'
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Keywords
            </button>
            <button
              onClick={() => setActiveTab('recommendations')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'recommendations'
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Recommendations
            </button>
            <button
              onClick={() => setActiveTab('insights')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'insights'
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              Insights
            </button>
            {aiSuggestions.length > 0 && (
              <button
                onClick={() => setActiveTab('ai')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'ai'
                    ? 'border-cyan-500 text-cyan-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  AI Assistant
                </span>
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Keywords Tab */}
          {activeTab === 'keywords' && (
            <div className="space-y-6">
              {/* Matched Keywords */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Matched Keywords</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {matchedKeywords.slice(0, 30).map((keyword, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-sm font-medium"
                    >
                      {typeof keyword === 'string' ? keyword : keyword.term || keyword}
                    </span>
                  ))}
                </div>
              </div>

              {/* Missing Keywords */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <XCircle className="w-5 h-5 text-red-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Missing Keywords</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {missingKeywords.map((keyword, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Recommendations Tab */}
          {activeTab === 'recommendations' && (
            <div className="space-y-4">
              {recommendations.map((rec, idx) => (
                <div key={idx} className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {rec.title || rec}
                    </h4>
                    {rec.description && (
                      <p className="text-sm text-gray-700">{rec.description}</p>
                    )}
                    {rec.impact && (
                      <p className="text-xs text-gray-600 mt-2">{rec.impact}</p>
                    )}
                  </div>
                </div>
              ))}
              {recommendations.length === 0 && (
                <p className="text-gray-600 text-center py-8">No recommendations available</p>
              )}
            </div>
          )}

          {/* Insights Tab */}
          {activeTab === 'insights' && (
            <div className="space-y-4">
              {/* Strengths */}
              {strengths.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Strengths</h3>
                  </div>
                  <ul className="space-y-2">
                    {strengths.map((strength, idx) => (
                      <li key={idx} className="flex gap-2 text-gray-700">
                        <span className="text-emerald-600 mt-1">•</span>
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Weaknesses */}
              {weaknesses.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Areas for Improvement</h3>
                  </div>
                  <ul className="space-y-2">
                    {weaknesses.map((weakness, idx) => (
                      <li key={idx} className="flex gap-2 text-gray-700">
                        <span className="text-amber-600 mt-1">•</span>
                        <span>{weakness}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {strengths.length === 0 && weaknesses.length === 0 && (
                <p className="text-gray-600 text-center py-8">No insights available</p>
              )}
            </div>
          )}

          {/* AI Assistant Tab */}
          {activeTab === 'ai' && (
            <div className="space-y-4">
              {aiSuggestions.map((suggestion, idx) => (
                <div key={idx} className="flex gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-gray-700">{suggestion}</p>
                  </div>
                </div>
              ))}
              {aiSuggestions.length === 0 && (
                <p className="text-gray-600 text-center py-8">No AI suggestions available</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 justify-center">
        <button 
          onClick={() => downloadReport(results, resumeName || 'Resume', jobTitle || 'Position')}
          className="px-6 py-3 bg-cyan-600 text-white rounded-xl font-semibold hover:bg-cyan-700 transition flex items-center gap-2"
        >
          <FileText className="w-5 h-5" />
          Export Report
        </button>
        <button 
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition"
        >
          Analyze Another Resume
        </button>
      </div>
    </div>
  );
}